/**
 * Pokédex Favorites — front-end client
 *
 * Architecture :
 *   - Backend Supabase edge fn `pokedex-favorites` (GET/POST/DELETE)
 *   - Cache localStorage (TTL 24h) avec data complète des Pokémon favoris
 *     → render instantané au refresh sans attendre le JSON hub (1025 Pokémon)
 *   - Optimistic UI : update visuel immédiat, sync API en background
 *   - Mode toggle via burger "Collection" button → body[data-ps-favori-mode="on"]
 *   - Affichage : tri par numéro de Pokédex national (id)
 *
 * État favoris :
 *   - non-connecté : aucune persistence, UI empty state "se connecter"
 *   - connecté    : fetch au load, sync à chaque toggle
 *
 * Hooks attendus côté DOM :
 *   - <body data-customer-id="123"> (rendered par Liquid si customer logged in)
 *   - .ps-pokedex-card[data-slug="pikachu"] cards Pokémon
 *   - .ps-pokedex-card__fav-btn boutons étoile par card
 *   - [data-ps-pokedex-fav-mode-toggle] burger button
 *   - .ps-pokedex-fav-section section "Mes Favoris" (rendered par hub section)
 */
(function() {
  'use strict';

  const SUPABASE_FN = 'https://ipvzpgwgujamtzztxvcu.supabase.co/functions/v1/pokedex-favorites';
  const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h - cache est invalidé à chaque toggle anyway
  const MODE_KEY = 'ps-pokedex-favori-mode';
  const STATE_KEY = (cid) => `ps-pokedex-favorites-${cid}`;

  // ─── State ─────────────────────────────────────────────────────────────────
  let customerId = null;
  let favorites = new Set();         // Set<slug>
  let favoritesData = [];            // Array<{slug, name, sprite, id, color, types}> - pour render instantané
  let pokemonIndex = null;           // Map slug → pokemon data (filled by hub on load)

  // ─── Sort util ─────────────────────────────────────────────────────────────
  function sortByDex(arr) {
    // Tri par numéro de Pokédex national (id), puis nom en départage.
    return arr.slice().sort((a, b) => (Number(a.id) || 99999) - (Number(b.id) || 99999) || (a.name || '').localeCompare(b.name || '', 'fr', { sensitivity: 'base' }));
  }

  // ─── Storage ───────────────────────────────────────────────────────────────
  function readCache() {
    if (!customerId) return null;
    try {
      const raw = localStorage.getItem(STATE_KEY(customerId));
      if (!raw) return null;
      const obj = JSON.parse(raw);
      if (Date.now() - obj.ts > CACHE_TTL_MS) return null;
      return obj.favorites; // peut être array<slug> (vieux format) ou array<obj> (nouveau format)
    } catch (e) { return null; }
  }
  function writeCache(data) {
    if (!customerId) return;
    try { localStorage.setItem(STATE_KEY(customerId), JSON.stringify({ ts: Date.now(), favorites: data })); } catch (e) {}
  }

  // ─── API ───────────────────────────────────────────────────────────────────
  async function apiGet() {
    if (!customerId) return [];
    const r = await fetch(`${SUPABASE_FN}?customer_id=${customerId}`, {
      headers: { 'x-ps-token': window.PS_AUTH_TOKEN || '' },
    });
    if (!r.ok) return [];
    const j = await r.json();
    return j.favorites || [];
  }
  // `customer_id` est usurpable ⇒ l'identité vient du jeton (métafield Shopify →
  // window.PS_AUTH_TOKEN), le cid du body est ignoré côté serveur.
  async function apiAdd(slug) {
    if (!customerId) return false;
    const r = await fetch(SUPABASE_FN, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-ps-token': window.PS_AUTH_TOKEN || '' },
      body: JSON.stringify({ customer_id: Number(customerId), pokedex_slug: slug })
    });
    return r.ok;
  }
  async function apiRemove(slug) {
    if (!customerId) return false;
    const r = await fetch(SUPABASE_FN, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', 'x-ps-token': window.PS_AUTH_TOKEN || '' },
      body: JSON.stringify({ customer_id: Number(customerId), pokedex_slug: slug })
    });
    return r.ok;
  }

  // ─── Mode toggle ───────────────────────────────────────────────────────────
  // Mode favoris = éphémère, jamais persisté. Toute arrivée sur la page = off.
  function setMode(on) {
    document.body.dataset.psFavoriMode = on ? 'on' : 'off';
    document.querySelectorAll('[data-ps-pokedex-fav-mode-toggle]').forEach(b => b.setAttribute('aria-pressed', String(on)));
  }
  function toggleMode() {
    const isOn = document.body.dataset.psFavoriMode === 'on';
    setMode(!isOn);
  }
  function initMode() {
    // Nettoie tout résidu d'une ancienne version qui persistait le mode
    try { localStorage.removeItem(MODE_KEY); } catch (e) {}
    setMode(false);
  }

  // ─── UI update ─────────────────────────────────────────────────────────────
  function updateCardState(slug, isFav) {
    document.querySelectorAll(`.ps-pokedex-card[data-slug="${slug}"]`).forEach(card => {
      card.dataset.psFav = isFav ? '1' : '0';
      const btn = card.querySelector('.ps-pokedex-card__fav-btn');
      if (btn) btn.setAttribute('aria-pressed', String(isFav));
    });
  }
  function updateAllCards() {
    document.querySelectorAll('.ps-pokedex-card[data-slug]').forEach(card => {
      const slug = card.dataset.slug;
      const isFav = favorites.has(slug);
      card.dataset.psFav = isFav ? '1' : '0';
      const btn = card.querySelector('.ps-pokedex-card__fav-btn');
      if (btn) btn.setAttribute('aria-pressed', String(isFav));
    });
  }

  // ─── Section "Mes Favoris" ─────────────────────────────────────────────────
  function buildFavCard(p) {
    if (!p) return '';
    const typeChips = (p.types || []).map(t => `<span class="ps-pokedex-card__type" style="background-color: ${t.color || '#888'}">${t.name}</span>`).join('');
    return `
      <a href="/collections/pokedex-${p.slug}" class="ps-pokedex-card" data-slug="${p.slug}" data-ps-fav="1" style="--card-color: ${p.color || '#9CA3AF'}">
        <button type="button" class="ps-pokedex-card__fav-btn" aria-label="Retirer ${p.name} des favoris" aria-pressed="true">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
        </button>
        <div class="ps-pokedex-card__sprite-wrap">
          <img src="${p.sprite}" alt="${p.name}" loading="lazy" class="ps-pokedex-card__sprite" draggable="false">
        </div>
        <div class="ps-pokedex-card__id">#${String(p.id).padStart(4, '0')}</div>
        <div class="ps-pokedex-card__name">${p.name}</div>
        <div class="ps-pokedex-card__types">${typeChips}</div>
      </a>
    `;
  }

  // Set data-ps-fav-state sur le hub root (CSS pilote la visibilité de la dashed box)
  function setFavState(state) {
    const hub = document.querySelector('[data-pokedex-hub]');
    if (hub) hub.dataset.psFavState = state;
  }

  function renderFavSection() {
    const wrap = document.querySelector('[data-ps-pokedex-fav-section]');
    if (!wrap) return;

    const gridEl = wrap.querySelector('[data-ps-fav-grid]');
    const emptyEl = wrap.querySelector('[data-ps-fav-empty]');
    if (!gridEl || !emptyEl) return;

    // Le contenu texte du empty box est rendu server-side en Liquid (selon customer).
    // JS ne fait que toggler l'état → pas de flash "grow-into-place".
    if (!customerId) {
      gridEl.innerHTML = '';
      setFavState('logged-out');
      return;
    }
    if (favorites.size === 0) {
      gridEl.innerHTML = '';
      setFavState('no-favs');
      // Vide le cache HTML pour ne pas injecter de cards stale au prochain refresh
      try { localStorage.removeItem('ps-pokedex-favorites-html-' + customerId); } catch (e) {}
      return;
    }
    // Favoris présents
    // 1) Data cachée → render instantané
    if (favoritesData.length > 0) {
      const sorted = sortByDex(favoritesData);
      gridEl.innerHTML = sorted.map(buildFavCard).join('');
      setFavState('has-favs');
      writeGridHtmlCache(gridEl.innerHTML);
      return;
    }
    // 2) Pas de data cachée mais pokemonIndex prêt → enrichit et render
    if (pokemonIndex) {
      favoritesData = [...favorites].map(s => pokemonIndex.get(s)).filter(Boolean);
      writeCache(favoritesData);
      const sorted = sortByDex(favoritesData);
      gridEl.innerHTML = sorted.map(buildFavCard).join('');
      setFavState('has-favs');
      writeGridHtmlCache(gridEl.innerHTML);
      return;
    }
    // 3) Ni cache data ni index, MAIS on sait qu'il y a des favoris → on PRÉSERVE le HTML
    //    déjà injecté par l'anti-flash inline script (qui a lu ps-pokedex-favorites-html-{cid}).
    //    Re-render au setPokemonIndex() (sera appelé quand pokedex-raw.json sera prêt).
    //    Fix bug 2026-05-31 : avant on faisait `gridEl.innerHTML = ''` → flash favoris qui disparaissent.
    setFavState('has-favs');
  }

  // Cache HTML rendu de la grid favoris (lu sync par l'inline script Liquid avant paint)
  function writeGridHtmlCache(html) {
    if (!customerId) return;
    try {
      localStorage.setItem('ps-pokedex-favorites-html-' + customerId, JSON.stringify({ ts: Date.now(), html: html }));
    } catch (e) { /* QuotaExceededError silencieux */ }
  }

  // Extrait les données d'un Pokémon depuis sa card DOM (fallback si pokemonIndex pas chargé)
  function extractPokemonFromCard(card) {
    if (!card) return null;
    try {
      const slug = card.dataset.slug;
      if (!slug) return null;
      const id = parseInt(card.dataset.id, 10) || 0;
      const name = (card.querySelector('.ps-pokedex-card__name')?.textContent || '').trim();
      const sprite = card.querySelector('.ps-pokedex-card__sprite')?.src || '';
      const color = card.style.getPropertyValue('--card-color').trim() || '#9CA3AF';
      const typesEls = card.querySelectorAll('.ps-pokedex-card__type');
      const types = [...typesEls].map(el => ({
        name: (el.textContent || '').trim(),
        color: el.style.backgroundColor || '#888'
      }));
      return { slug, id, name, sprite, color, types };
    } catch (e) { return null; }
  }

  // ─── Toggle favori ─────────────────────────────────────────────────────────
  async function toggleFavorite(slug, btn) {
    if (!customerId) {
      window.location.href = '/account/login';
      return;
    }
    const wasFav = favorites.has(slug);

    // Récupère la data du Pokémon : depuis pokemonIndex sinon depuis la card DOM
    // (fallback indispensable : permet update live même si le JSON 1025 du hub n'est pas encore chargé)
    let pdata = pokemonIndex ? pokemonIndex.get(slug) : null;
    if (!pdata && btn) {
      pdata = extractPokemonFromCard(btn.closest('.ps-pokedex-card'));
    }

    // Optimistic UI
    if (wasFav) {
      favorites.delete(slug);
      favoritesData = favoritesData.filter(p => p.slug !== slug);
    } else {
      favorites.add(slug);
      if (pdata) favoritesData.push(pdata);
    }
    updateCardState(slug, !wasFav);
    renderFavSection();
    // API call
    const ok = wasFav ? await apiRemove(slug) : await apiAdd(slug);
    if (!ok) {
      // Rollback
      if (wasFav) {
        favorites.add(slug);
        if (pdata) favoritesData.push(pdata);
      } else {
        favorites.delete(slug);
        favoritesData = favoritesData.filter(p => p.slug !== slug);
      }
      updateCardState(slug, wasFav);
      renderFavSection();
      console.error('[pokedex-fav] API failed for', slug);
      return;
    }
    writeCache(favoritesData);
  }

  // ─── Public API (called by hub section) ────────────────────────────────────
  window.PSPokedexFavorites = {
    setPokemonIndex(map) {
      pokemonIndex = map;
      // Si on a des slugs sans data complète (vieux cache), enrichit maintenant
      if (favorites.size > 0 && favoritesData.length !== favorites.size) {
        favoritesData = [...favorites].map(s => pokemonIndex.get(s)).filter(Boolean);
        writeCache(favoritesData);
      }
      renderFavSection();
      updateAllCards();
    },
    isFavorite(slug) { return favorites.has(slug); },
    getCustomerId() { return customerId; },
    refresh() { return loadFromServer(); }
  };

  async function loadFromServer() {
    if (!customerId) return;
    const arr = await apiGet();
    const serverSet = new Set(arr);
    favorites = serverSet;
    if (pokemonIndex) {
      favoritesData = arr.map(s => pokemonIndex.get(s)).filter(Boolean);
      writeCache(favoritesData);
    } else {
      // Garde la favoritesData existante mais filtre selon serveur
      favoritesData = favoritesData.filter(p => serverSet.has(p.slug));
      // Si serveur a des slugs nouveaux non présents en cache, on les chopera quand pokemonIndex arrive
    }
    updateAllCards();
    renderFavSection();
  }

  // ─── Init ──────────────────────────────────────────────────────────────────
  function init() {
    const hub = document.querySelector('[data-pokedex-hub]');
    customerId = (hub?.dataset?.customerId || document.body.dataset.customerId || '').trim();
    if (customerId === '' || customerId === 'null') customerId = null;
    initMode();

    // Read cache first for instant UI
    const cached = readCache();
    if (cached && cached.length > 0) {
      if (typeof cached[0] === 'string') {
        // Vieux format : juste les slugs
        favorites = new Set(cached);
        favoritesData = [];
      } else {
        // Nouveau format : data complète → render instantané
        favoritesData = cached;
        favorites = new Set(cached.map(p => p.slug));
      }
      updateAllCards();
      renderFavSection();
    }

    // Then fetch fresh from server
    if (customerId) loadFromServer();

    document.addEventListener('ps-pokedex-rerendered', () => { updateAllCards(); });

    document.addEventListener('click', (e) => {
      const toggleBtn = e.target.closest('[data-ps-pokedex-fav-mode-toggle]');
      if (toggleBtn) {
        e.preventDefault();
        e.stopPropagation();
        // VISITEUR (17/07, signalé par Lorenzo) : le bouton « Collection » du burger basculait
        // l'affichage SANS aucun test de connexion → on affichait une liste de favoris
        // forcément vide, sans rien expliquer. Idem pour l'étoile d'une carte : `toggleFavorite`
        // faisait `if (!customerId) return false;` — muet. C'est pourtant LE moment où le
        // visiteur demande la fonctionnalité : on lui montre le pitch au lieu de rien.
        if (!customerId) { if (window.psGuestSignup) window.psGuestSignup('pokedex-burger'); return; }
        toggleMode();
        return;
      }
      const favBtn = e.target.closest('.ps-pokedex-card__fav-btn');
      if (favBtn) {
        e.preventDefault();
        e.stopPropagation();
        if (!customerId) { if (window.psGuestSignup) window.psGuestSignup('pokedex-etoile'); return; }
        const card = favBtn.closest('.ps-pokedex-card');
        const slug = card?.dataset?.slug;
        if (slug) toggleFavorite(slug, favBtn);
      }
    }, true);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
